import type Phaser from 'phaser';
import { BALLOON_KEYS, CAKE_KEY } from './birthday-art.js';
import { type Decoration } from './birthday-decor.js';
import { decorDepth } from './depth.js';

/** Where each balloon's string ends, relative to the bunch's anchor, back to front. */
const BALLOON_OFFSETS: readonly { readonly x: number; readonly y: number }[] = [
  { x: 0, y: -12 },
  { x: -8, y: 0 },
  { x: 8, y: -3 },
];
const BOB_PX = 1;
const BOB_MS = 1_400;

/**
 * Draws today's birthday decorations (see `birthdayDecorations`): balloon bunches that bob gently
 * (still with reduced motion) and cakes, depth-sorted like the furniture they stand on.
 */
export class BirthdayLayer {
  private objects: (Phaser.GameObjects.Image | Phaser.GameObjects.Container)[] = [];
  private key = '';

  constructor(private readonly scene: Phaser.Scene) {}

  /** Replaces the decorations; the same set again changes nothing. */
  set(decorations: readonly Decoration[], reducedMotion: boolean): void {
    const key = JSON.stringify(decorations);
    if (key === this.key) return;
    this.key = key;
    this.clear();
    for (const decoration of decorations) {
      if (decoration.kind === 'cake') this.addCake(decoration);
      else this.addBalloons(decoration, reducedMotion);
    }
  }

  destroy(): void {
    this.clear();
  }

  private addCake({ x, y, row }: Decoration): void {
    if (!this.scene.textures.exists(CAKE_KEY)) return;
    this.objects.push(
      this.scene.add.image(x, y, CAKE_KEY).setOrigin(0.5, 1).setDepth(decorDepth(row)),
    );
  }

  private addBalloons({ x, y, row }: Decoration, reducedMotion: boolean): void {
    const images = BALLOON_KEYS.flatMap((key, index) => {
      const offset = BALLOON_OFFSETS[index];
      if (!offset || !this.scene.textures.exists(key)) return [];
      return [this.scene.add.image(offset.x, offset.y, key).setOrigin(0.5, 1)];
    });
    if (images.length === 0) return;
    const bunch = this.scene.add.container(x, y, images).setDepth(decorDepth(row));
    this.objects.push(bunch);
    if (reducedMotion) return;
    images.forEach((image, index) =>
      this.scene.tweens.add({
        targets: image,
        y: image.y - BOB_PX,
        duration: BOB_MS + index * 230,
        delay: index * 400,
        ease: 'Sine.easeInOut',
        yoyo: true,
        repeat: -1,
      }),
    );
  }

  private clear(): void {
    for (const object of this.objects) {
      if ('list' in object) this.scene.tweens.killTweensOf(object.list);
      object.destroy();
    }
    this.objects = [];
  }
}
